<template>
	<div v-if="!loading">
		<div class="flex">
			<div class="col">
				<h3>{{getCompanyType}}: {{companyRecord.companyName}}</h3>
			</div>
			<div class="col-fixed" v-if="!loading && isInternalUser">
				<label>Active<br /></label>
				<InputSwitch v-model="companyRecord.active" />
			</div>
		</div>

		<div class="flex flex-row flex-wrap" v-if="!loading">
			<div class="col-12 xl:col-7">
				<div class="card">
					<div class="fluid grid">
						<div class="field col-12">
							<a @click="enableNameEdit">
								<i class="pi pi-lock" v-if="nameLocked"></i>
								<i class="pi pi-lock-open" v-if="!nameLocked"></i>
							</a>
							<div class="float-right label" v-if="companyId > 0"># {{ companyId }}</div>
							<label>Company name</label>
							<InputText v-model="companyRecord.companyName" :disabled="nameLocked" />
						</div>
						<div class="field col-12 md:col-6">
							<label>Company short name</label>
							<InputText v-model="companyRecord.companyShortName" />
						</div>

						<div class="field col-12 md:col-6">
							<label>Relationship</label><br />
							<div class="flex flex-nowrap">
								<ToggleButton v-model="companyRecord.isCustomer" onLabel="Customer" offLabel="Customer" />
								&nbsp;
								<ToggleButton v-model="companyRecord.isSupplier" onLabel="Supplier" offLabel="Supplier" />
								&nbsp;
								<ToggleButton v-model="companyRecord.isOwner" onLabel="Owner" offLabel="Owner" />
							</div>
						</div>

						<div class="field col-12 md:col-6">
							<label>Company Phone</label>
							<InputText v-model="companyRecord.companyPhone" />
						</div>

						<div class="field col-12 md:col-6">
							<label>Account No</label>
							<InputText v-model="companyRecord.accountNumber" />
						</div>

						<div class="field col-12 md:col-6">
							<label>Street Address</label>
							<InputText v-model="companyRecord.streetAddress" />
						</div>

						<div class="field col-12 md:col-6">
							<label>Postal Address</label>
							<InputText v-model="companyRecord.postalAddress" />
						</div>

						<div class="field col-12 md:col-6 xl:col-4">
							<label>Suburb</label>
							<InputText v-model="companyRecord.suburb" />
						</div>

						<div class="field col-12 md:col-6 xl:col-4">
							<label>City/Town</label>
							<InputText v-model="companyRecord.city" />
						</div>

						<div class="field col-12 md:col-6 xl:col-2">
							<label>Postcode</label>
							<InputText v-model="companyRecord.postCode" />
						</div>

						<div class="field col-12 md:col-6 xl:col-2">
							<label>Authority</label><br />
							<InputSwitch v-model="companyRecord.isAuthority" v-tooltip.top="'Mark as a Regional Authority (Council). This also determines North Island vs South Island location of properties assigned with this regional authority.'" />
						</div>

						<div class="field col-12 md:col-6 xl:col-4">
							<label>Island</label><br />
							<div class="flex flex-nowrap">
								<ToggleButton v-model="northIsland" onLabel="North Island" offLabel="North Island" v-tooltip.top="'This company operates in the North Island'" @change="onIslandUpdate" />
								&nbsp;
								<ToggleButton v-model="southIsland" onLabel="South Island" offLabel="South Island" v-tooltip.top="'This company operates in the South Island'" @change="onIslandUpdate" />
							</div>
						</div>

						<div class="field col-12">
							<label>Company Notes</label>
							<TextArea v-model="companyRecord.companyNotes" rows="6" :autoResize="true" />
						</div>
						<div class="field col-12">
							<Button class="float-left" icon="pi pi-trash" label="Delete" @click="deleteCompany" v-if="!newCompany" />
							<Button class="float-right" label="Pricing Summary" @click="pricingSummaryClick" />
						</div>
					</div>
				</div>
			</div>
			<div class="col-12 xl:col-5" style="align-self:stretch">

				<div class="card">
					<!-- Property Header -->
					<div style="margin-bottom:5px;">
						<div class="flex align-items-end ">
							<div style="padding:5px;margin-right:15px;">
								Associated Properties
							</div>
							<div class="flex flex-auto p-input-icon-left p-input-icon-right">
								<i class="pi pi-home" />
								<InputText v-model="propertyListFilter" placeholder="Property keyword filter.." @input="filterProperties" />
								<i class="pi pi-search" />
							</div>
						</div>
					</div>

					<!-- Property List -->
					<div style="height:536px;">
						<DataTable :value="filteredAssociatedProperties"
								   :scrollable="true"
								   :style="{cursor:'pointer'}"
								   @row-click="propertyClick"
								   datakey="propertyId"
								   scrollHeight="flex"
								   stripedRows
								   class="p-datatable-sm"
								   responsiveLayout="scroll"
								   currentPageReportTemplate="Showing {first} to {last} of {totalRecords}">
							<Column field="propertyId" header="ID" style="flex-grow:0;flex-basis:80px"></Column>
							<Column field="propertyNameAndLocation" header="Property"></Column>
						</DataTable>
					</div>
				</div>

				<!-- Email automation flag 1: for suppliers (currently shown only to suppliers with related properties) -->
				<div class="flex field" v-if="this.companyRecord.isSupplier && companyRecord.associatedProperties && companyRecord.associatedProperties.length > 0">
					<InputSwitch v-model="companyRecord.enableEmailAutomation" />
					<div class="label-beside-input-switch">Enable supplier email automation (12A requests)</div>
				</div>

				<!-- Email automation flag 2: for customers, CC this customer on urgent and overdue emails  -->
				<div class="flex field" v-if="this.companyRecord.isCustomer && companyRecord.associatedProperties && companyRecord.associatedProperties.length > 0">
					<InputSwitch v-model="companyRecord.enableCustomerCcEmails" />
					<div class="label-beside-input-switch">CC this customer on urgent and overdue emails (12A requests)</div>
				</div>

				<!-- Email automation flag 3: for customers, CC this customer on follow-up emails (14 days) -->
				<div class="flex field" v-if="this.companyRecord.isCustomer && companyRecord.associatedProperties && companyRecord.associatedProperties.length > 0">
					<InputSwitch v-model="companyRecord.ccCustomerOn12aFollowUp" />
					<div class="label-beside-input-switch">CC this customer on 14 days follow-up emails (12A requests)</div>
				</div>
			</div>
		</div>

		<!--Contacts-->
		<div class="col-12">
			<div class="card">
				<Contacts title="Contacts" :contacts="companyRecord.contacts" view="toggle" :canEdit="true" />
			</div>
		</div>

		<!--Billing Entities-->
		<div class="col-12" v-if="!newCompany">
			<div class="card">
				<BillingEntities title="Billing Entities" :billingEntities="companyRecord.billingEntities" />
			</div>
		</div>
	</div>

	<!-- Floating Save/Cancel buttons -->
	<Button class="sticky-button cancel" icon="pi pi-times" :label="hasChanges ? 'Cancel' : 'Close'" @click="cancel" v-if="!loading" />
	<Button class="sticky-button" icon="pi pi-check" label="Save" @click="save" v-if="!loading && !newCompany" :disabled="!hasChanges" />
	<Button class="sticky-button" icon="pi pi-check" label="Add new record" @click="saveNewCompany" v-if="!loading && newCompany" />

</template>

<script lang="ts">
	import { Vue } from 'vue-class-component';
	import { CompassApi } from '@/api-client/CompassApiProxy'
	import { BillingEntityData, CompanyData, ContactData, PropertySummary } from '../api-client/api-client'
	import { email, required } from "@vuelidate/validators";
	import { allowLeaveOnlyWithConfirmationOrNoChanges } from '@/components/LeaveWithoutSavingPrompt';
	import Toast from '@/utils/Toast';
	import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
	import { DataTableRowClickEvent } from 'primevue/datatable';
	import { ConfirmationOptions } from 'primevue/confirmationoptions';
	import { Prop } from 'vue-property-decorator';
	import { CompanyType } from '@/utils/CompassConstants';

	Vue.registerHooks([
		'beforeRouteLeave',
	]);

	export default class CompanyDetails extends Vue {

		@Prop({ type: Number, required: true }) companyId!: number;
		@Prop({ type: Number, required: false }) companyType!: number | null;

		newCompany = false;

		receivedDataJSON = "";
		companyRecord: CompanyData = new CompanyData();

		propertyListFilter = "";

		nameLocked = true;
		loading = true;

		northIsland = false;
		southIsland = false;

		isInternalUser = false;

		filteredAssociatedProperties: PropertySummary[] = [];

		created() {
			if (this.companyId < 0) {
				this.setNewCompany();
				return;
			}

			// Set intenalUser flag based on token
			this.isInternalUser = this.$store.getters.isInternalUser;

			// Set initial name (if available)
			const list = this.$store.state.companyList.data;
			try {
				const rec = list.filter((r) => r.id === this.companyId);
				if (rec.length > 0)
					this.companyRecord.companyName = rec[0].name;
			}
			catch (e) { console.error('Note company list not loaded:' + e) }

			// Retrieve data from API
			const apiClient = CompassApi.companyApi(this.$store);
			apiClient.getCompanyById(this.companyId)
				.then(result => {
					if (result && result.contacts) {
						this.loading = false;
						this.companyRecord = result;
						this.filteredAssociatedProperties = result.associatedProperties ?? [];
						this.getIsland();
					}

					this.receivedDataJSON = JSON.stringify(result, null, 1);
				})
				.catch(error => {
					this.loading = false;
					Toast.ApiError('Error loading company ' + this.companyId, error);
				});
			this.loading = false;
		}

		setNewCompany() {
			this.newCompany = true;

			this.nameLocked = false;
			this.companyRecord.active = true;

			this.companyRecord.isCustomer = this.companyType === CompanyType.Customer;
			this.companyRecord.isSupplier = this.companyType === CompanyType.Supplier;
			this.companyRecord.isOwner = this.companyType === CompanyType.Owner;

			this.companyRecord.contacts = [];

			this.companyRecord.enableEmailAutomation = true;
			this.receivedDataJSON = JSON.stringify(this.companyRecord, null, 1);

			this.loading = false;
			return;
		}

		  beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
			allowLeaveOnlyWithConfirmationOrNoChanges(this, this.hasChanges, next);
		}

		propertyClick(ev: DataTableRowClickEvent) {
			this.$router.push({
				name: 'PropertyDetails',
				params: { propertyId: ev.data.propertyId }
			});
		}

		enableNameEdit(): void {
			this.nameLocked = !this.nameLocked;
		}

		validations() {
			return {
				name: {
					required
				},
				email: {
					required,
					email
				},
				password: {
					required
				},
				accept: {
					required
				}
			}
		}

		// Actions
		save() {
			// Remove last contact if it's empty
			this.removeEmptyContact();
			this.removeEmptyBillingEntity();

			// Don't need to save (and change whenUpdated) if there are no changes
			if (!this.hasChanges) {
				this.cancel();
				return;
			}

			if (this.companyId < 0) {
				console.error(`Company identifier is less than 0. This should not be possible.`)
				return;
			}

			const isCompanyDeleted = this.companyRecord.whenDeleted !== null;
			// save company data
			const apiClient = CompassApi.companyApi(this.$store);
			apiClient.updateCompany(this.companyRecord)
				.then(() => {
					if (isCompanyDeleted)
						this.cancel();
					this.$store.dispatch('downloadCompanyList');

					// Remember the data as saved, so we only enable save button etc if there are changes after this
					this.receivedDataJSON = JSON.stringify(this.companyRecord, null, 1);

					Toast.RemoveAllGroups();
					Toast.Success('Save', 'Company details have been saved.');
				})
				.catch(error => {
					Toast.ApiError('Error saving company ' + this.companyId, error);
				});
		}

		cancel() {
			this.$router.go(-1);
		}

		saveNewCompany() {
			// create company record
			const apiClient = CompassApi.companyApi(this.$store);
			apiClient.addCompany(this.companyRecord)
				.then(result => {
					this.$store.dispatch('downloadCompanyList');

					// result has the new companyid
					const newUrl = window.location.href.replace("/new", "/" + JSON.stringify(result));
					window.location.replace(newUrl);
				})
				.catch(error => {
					Toast.ApiError('Error creating company ' + this.companyRecord.companyName, error);
				});
		}

		removeEmptyContact() {
			if (this.companyRecord.contacts != null) {
				const lastContact: ContactData = this.companyRecord.contacts[this.companyRecord.contacts.length - 1];
				if (lastContact != null && lastContact.firstName == null && lastContact.lastName == null && lastContact.email == null) {
					this.companyRecord.contacts?.splice(this.companyRecord.contacts.length - 1, 1);
				}
			}
		}
		removeEmptyBillingEntity() {
			if (this.companyRecord.billingEntities != null) {
				const lastEntity: BillingEntityData = this.companyRecord.billingEntities[this.companyRecord.billingEntities.length - 1];
				if (lastEntity != null && lastEntity.billingEntityName == null) {
					this.companyRecord.billingEntities?.splice(this.companyRecord.billingEntities.length - 1, 1);
				}
			}
		}

		pricingSummaryClick() {
			this.$router.push({
				name: 'CompanyPricingSummary',
				params: { companyId: this.companyId }
			});
		}

		deleteCompany() {
			this.$confirm.require({
				header: 'Delete company',
				message: 'Are you sure you want to delete this company ' + this.companyRecord.companyName + ' ?',
				accept: () => {
					this.companyRecord.whenDeleted = new Date();
					this.companyRecord.active = false;
					this.save();
				},
				reject: () => {
					return;
				}
			} as ConfirmationOptions);
		}

		getIsland() {
			if (this.companyRecord.island) {
				if (this.companyRecord.island.includes('NI')) {
					this.northIsland = true;
				}
				if (this.companyRecord.island.includes('SI')) {
					this.southIsland = true;
				}
			}
		}

		onIslandUpdate() {
			let island = "";
			if (this.northIsland) {
				island += "NI "
			}
			if (this.southIsland) {
				island +="SI"
			}
			island = island.trim();

			this.companyRecord.island = island == "" ? undefined : island;
		}

		// Computed
		get getCompanyType() {
			if (this.loading)
				return "Loading..";

			const type = (this.companyRecord.isAuthority ? "A" : "")
				+ (this.companyRecord.isCustomer ? "C" : "")
				+ (this.companyRecord.isSupplier ? "S" : "")
				+ (this.companyRecord.isOwner ? "O" : "")

			if (type == "A")
				return "Authority";

			if (type == "O")
				return "Owner";

			if (type == "C")
				return "Customer";

			if (type == "S")
				return "Supplier";

			return "Company";
		}

		filterProperties() {
			const keyword = this.propertyListFilter.toLowerCase().trim();
			const associatedProperties = this.companyRecord.associatedProperties ?? [];

			this.filteredAssociatedProperties = !keyword
				? associatedProperties
				: associatedProperties.filter(property =>
					property.propertyNameAndLocation?.toLowerCase().includes(keyword) ||
					property.propertyId?.toString().includes(keyword)
				);
		}

		get hasChanges(): boolean {
			const currentJSON = JSON.stringify(this.companyRecord, null, 1);
			return (currentJSON != this.receivedDataJSON);
		}
	}
</script>

<style scoped lang="scss">
</style>